import { Injectable } from "@angular/core";
import Swal from 'sweetalert2';
import { InjectorInstance } from '../app.module';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from "@ngx-translate/core";

const toastOptions: any = {
  timeOut: 5000,
  positionClass: 'toast-bottom-right',
  progressBar: true
}

/**
* Servizio per i messaggi di dialogo
*/
@Injectable({ providedIn: 'root' })
export class DialogService {

  static toastrService: ToastrService = null;
  static translate: TranslateService = null;

  constructor(public translate: TranslateService) {

  }
  /**
   * Utilizza l'InjectorInstance definita nell'app.module per iniettare i servizi che sono necessari in questa classe.
   * Il metodo è stato necessario perchè questo servizio espone metodi statici quindi non sarebbe stato possibile usare l'inject con il costruttore della classe
   */
  static injectServices() {
    //Inietta il translate service ogni volta che viene richiesto altrimenti, essendo il dialogService statico si rischia che facendo logout e login cambiando lingua
    //l'istanza del translateservice usata rimane agganciata al vecchio dizionario
    this.toastrService = InjectorInstance.get<ToastrService>(ToastrService);
    this.translate = InjectorInstance.get<TranslateService>(TranslateService);
  }

  /**
   * Mostra uno sweet alert di tipo SUCCESS con il messaggio passato come parametro.
   * @param message Messaggio da mostrare nello sweetalert
   * @param title Titolo dello sweetalert (di default mostra 'Operazione completata')
   */
  static Success(message: string, title: string = null, isToast: boolean = true, id: string = null) {
    this.injectServices();
    let currentTitle = title != null ? title : this.translate.instant("DIALOGS.OPERATION_COMPLETE");

    if (message.toLowerCase() === currentTitle.toLowerCase())
      message = null;

    if (isToast == null || isToast != true && id != null)
      Swal.fire({
        title: currentTitle, text: message, icon: 'success', inputAttributes: {
          id: id
        }
      });
    else if (isToast == null || isToast != true && id == null) {
      Swal.fire(currentTitle, message, 'success')
    }
    else {
      this.toastrService.success(message, currentTitle, toastOptions);
    }
  }

  /**
   * Mostra uno sweet alert di tipo ERROR con il messaggio passato come parametro.
   * @param message Messaggio d'errore da mostrare nello sweetalert
   * @param title Titolo dello sweetalert (di default mostra 'Errore')
   */
  static Error(message: string | string[], title: string = null) {
    this.injectServices();
    let currentTitle = title != null ? title : this.translate.instant("DIALOGS.ERROR");
    if (Array.isArray(message)) {
      currentTitle = title != null ? title : this.translate.instant("DIALOGS.ERROR_OCCURRED");
      let htmlErrors: string = message.join("<br>");
      Swal.fire({
        title: currentTitle,
        html: htmlErrors,
        icon: 'error'
      });
    }
    else {
      Swal.fire(currentTitle, message, 'error');
    }
  }

  /**
   * Mostra uno sweetalert di tipo INFO con il messaggio passato come parametro
   * @param message Messaggio da mostrare nello sweetalert
   * @param title Titolo dello sweetalert (di default mostra 'Info')
   */
  static Info(message: string, title: string = null, isToast: boolean = null) {
    this.injectServices();
    let currentTitle = title != null ? title : this.translate.instant("DIALOGS.INFORMATION");
    if (isToast == null || isToast != true)
      Swal.fire(currentTitle, message, 'info');
    else {
      this.toastrService.info(message, null, toastOptions);
    }
  }

  /**
   * Mostra uno sweetalert di tipo WARNING con il messaggio passato come parametro
   * @param message Messaggio da mostrare nello sweetalert
   * @param title Titolo dello sweetalert (di default mostra 'Attenzione!')
   */
  static Warning(message: string | string[], title: string = null, isToast: boolean = null) {
    this.injectServices();
    let currentTitle = title != null ? title : this.translate.instant("GENERIC_LABEL.ATTENTION");

    if (Array.isArray(message)) {
      currentTitle = title != null ? title : this.translate.instant("GENERIC_LABEL.ATTENTION");
      let htmlWarnings: string = message.join("<br>");      
      Swal.fire({
        title: currentTitle,
        html: htmlWarnings,
        icon: 'warning'
      });
    }
    else if (isToast == null || isToast != true) {
      Swal.fire(currentTitle, message, 'warning');
    }
    else
      this.toastrService.warning(message, currentTitle, toastOptions);
  }

  /**
   * Mostra uno sweetalert di tipo CONFIRM con il messaggio passato come parametro e se viene premuto
   * CONFERMA lancia la funzione di callback passata come parametro
   * @param message Messaggio da mostrare nello sweetalert
   * @param title Titolo dello sweetalert (di default mostra 'Richiesta conferma')
   */
  static Confirm(message: string | string[], confirmCallback: any, isWarning: boolean = false, title: string = null, customWidth: string = null, includeResultValue: boolean = false, confirmButtonText: string = null, isHtmlText: boolean = false) {
    this.injectServices();

    let currentTitle = title != null ? title : this.translate.instant("DIALOGS.CONFIRMATION_REQUEST");
    if (Array.isArray(message)) {
      message = message.filter(x => x != "");
      let htmlErrors = "<ul>";
      for(let m of message) {
        htmlErrors += "<li>";
        htmlErrors += m;
        htmlErrors += ";</li>";
        // htmlErrors += message.join("<br>");
      }
      htmlErrors += "</ul>";
      Swal.fire({
        title: currentTitle,
        html: htmlErrors,
        width: customWidth ? customWidth : '32rem',
        icon: !isWarning ? 'question' : 'warning',
        showCancelButton: true,
        confirmButtonText: confirmButtonText ? confirmButtonText : "Ok",
        cancelButtonText: this.translate.instant("GENERIC_LABEL.ABORT"),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {content: "flex-dialog-content"}
      }).then((result) => {
        if (result.value && confirmCallback) {
          confirmCallback();
        }
      });
    }
    else {
      if (isHtmlText) {       
        Swal.fire({
            title: currentTitle,
            html: message,
            width: customWidth ? customWidth : '32rem',
            icon: !isWarning ? 'question' : 'warning',
            showCancelButton: true,
            confirmButtonText: confirmButtonText ? confirmButtonText : "Ok",
            cancelButtonText: this.translate.instant("GENERIC_LABEL.ABORT"),
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then((result) => {
            if (result.isDismissed == true && includeResultValue == true) {
              confirmCallback(result);
            } else if (result.value && confirmCallback) {
              confirmCallback();
            }
          })
       }
       else {
        Swal.fire({
            title: currentTitle,
            text: message,
            width: customWidth ? customWidth : '32rem',
            icon: !isWarning ? 'question' : 'warning',
            showCancelButton: true,
            confirmButtonText: confirmButtonText ? confirmButtonText : "Ok",
            cancelButtonText: this.translate.instant("GENERIC_LABEL.ABORT"),
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then((result) => {
            if (result.isDismissed == true && includeResultValue == true) {
              confirmCallback(result);
            } else if (result.value && confirmCallback) {
              confirmCallback();
            }
          })
       }
    }


  }

  
  /**
   *
   * @param message Messaggio da mostrare nel prompt
   * @param confirmCallback Funzione di callback da richiamare sul conferma
   */
  static Prompt(message: string, confirmCallback: any) {
    this.injectServices();
    Swal.fire({
      title: message,
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off'
      },
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Esci",
      showLoaderOnConfirm: true,
      allowOutsideClick: false
    }).then((result) => {
      if (result.value && confirmCallback) {
        confirmCallback(result.value);
      }
    })
  }


  /**
 * Mostra un toast message che annuncia la ricezione di una nuova notifica.
 * Se passata un action da eseguire (es. aprire il dialog della notifica ricevuta) viene eseguita
 * se l'utente clicca sulla notifica e non se la chiude
 * @param message
 * @param currentTitle
 * @param closeToastAction
 */
  static ShowNewNotification(message: string = null, currentTitle: string = null, closeToastAction: any = null) {
    this.injectServices();
    if (!currentTitle)
      currentTitle = "Hai 1 nuova notifica";

    if (this.toastrService)
      this.toastrService.info(message, currentTitle, toastOptions);
  }
}
